import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Spinner } from 'react-bootstrap';
import { AlertTriangle, CheckCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import AccountPickerScope from '../../Common/AccountPickerScope';
import AccountSelect from '../../Common/AccountSelect';
import { amountToCents, sanitizeAmountInput } from '../manualBookkeepingUtils';
import './guided-preview-step.css';

/*
 * Granska — the last look before "Skapa verifikat".
 *
 * Two sections: what the user entered (read-only facts, in the same order as
 * the Detaljer step) and the kontering the template proposes. The kontering
 * is a plain table until the user asks to change it; then it becomes the same
 * editable row layout the manual entry page uses, saved back into
 * `entry.customJournalEntries` as numbers.
 *
 * Shape-agnostic by design: new-shape templates arrive with
 * `customJournalEntries` already computed by /suggest (NewTemplatePreviewStep);
 * legacy templates are evaluated here from `transactions[]` formulas.
 */

const sek = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kr = (amount) => `${sek.format(Number(amount) || 0)} kr`;
const roundCents = (n) => Math.round((Number(n) || 0) * 100);
const UNSET_ACCOUNT = 'Ej valt';

const sortByAccount = (rows) => [...rows].sort((a, b) => {
  const aUnset = !a.account || a.account === UNSET_ACCOUNT;
  const bUnset = !b.account || b.account === UNSET_ACCOUNT;
  if (aUnset !== bUnset) return aUnset ? 1 : -1;
  const na = parseInt(a.account, 10);
  const nb = parseInt(b.account, 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a.account).localeCompare(String(b.account));
});

// Legacy templates carry debit/credit formulas over the parameters.
const evaluateFormula = (formula, parameters) => {
  if (!formula) return 0;
  try {
    let expression = formula;
    Object.keys(parameters || {}).forEach((param) => {
      const value = parameters[param] || 0;
      expression = expression.replace(new RegExp(param, 'g'), value);
    });
    // eslint-disable-next-line no-eval
    return eval(expression);
  } catch (error) {
    console.error('Error evaluating formula:', formula, error);
    return 0;
  }
};

const legacyJournalEntries = (entry, accounts) => {
  const transactions = entry.templateData?.transactions;
  if (!Array.isArray(transactions)) return [];
  const rows = [];
  transactions.forEach((transaction, index) => {
    let account = transaction.account_number;
    let accountName = transaction.account_name;
    if (transaction.is_user_defined) {
      const chosen = entry.userDefinedAccounts?.[`user_account_${index}`];
      if (chosen) {
        account = chosen;
        const info = accounts.find((acc) => acc.value === chosen);
        accountName = info ? info.accountName || info.label.split(' - ')[1] : `Konto ${chosen}`;
      } else {
        account = UNSET_ACCOUNT;
        accountName = '';
      }
    }
    const debit = transaction.debit_formula ? evaluateFormula(transaction.debit_formula, entry.parameters) : 0;
    const credit = transaction.credit_formula ? evaluateFormula(transaction.credit_formula, entry.parameters) : 0;
    if (debit > 0 || credit > 0) {
      rows.push({ id: `entry_${index}`, account, accountName, debit, credit, isUserDefined: transaction.is_user_defined, originalIndex: index });
    }
  });
  return sortByAccount(rows);
};

// The inputs the user answered on Detaljer, with the template's labels and
// human values (option label, account number + name, formatted amount).
const answeredInputs = (entry, accounts) => {
  const body = entry.templateData?.body;
  if (!body || !Array.isArray(body.inputs)) return [];
  return body.inputs
    .map((input) => {
      const raw = entry.parameters?.[input.name];
      if (raw === undefined || raw === null || raw === '') return null;
      let value = String(raw);
      let isAmount = false;
      if (input.type === 'select') {
        value = (input.options || []).find((o) => String(o.value) === String(raw))?.label || value;
      } else if (input.type === 'account_picker') {
        const acc = accounts.find((a) => a.value === raw || a.accountNumber === raw);
        value = acc ? `${acc.accountNumber} ${acc.accountName}` : value;
      } else if (['currency', 'currency_gross', 'currency_net'].includes(input.type)) {
        value = kr(raw);
        isAmount = true;
      } else if (input.type === 'integer' || input.type === 'number') {
        value = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(Number(raw));
      }
      // "Skattefritt belopp per mil (schablon 25 kr/mil enligt IL 12 kap 5 §)"
      // is the right label on the form; here the value is what matters.
      const shortLabel = String(input.label || '').replace(/\s*\([^)]*\)/g, '').trim() || input.label;
      return { name: input.name, label: shortLabel, fullLabel: input.label, value, isAmount };
    })
    .filter(Boolean);
};

// The rows the template itself proposes — what "Återställ" goes back to and
// what "justerad av dig" is measured against.
const templateRows = (entry, legacyRows) => {
  if (entry.templateData?.body) {
    return (entry.bookingProposal?.rows || []).map((row) => ({
      account: String(row.account_number || ''),
      accountName: row.account_name || '',
      debit: Number(row.debit) || 0,
      credit: Number(row.credit) || 0,
      description: row.description || entry.description || '',
    }));
  }
  return legacyRows || [];
};

const sameRows = (a, b) => a.length === b.length && a.every((row, i) =>
  String(row.account) === String(b[i].account)
  && roundCents(row.debit) === roundCents(b[i].debit)
  && roundCents(row.credit) === roundCents(b[i].credit));

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'kB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

// Editing keeps amounts as typed text; saving turns them back into numbers.
const toDraftRows = (rows) => rows.map((row, index) => ({
  key: row.id || `row_${index}`,
  account: row.account === UNSET_ACCOUNT ? '' : (row.account || ''),
  accountName: row.accountName || '',
  debit: row.debit > 0 ? sek.format(row.debit) : '',
  credit: row.credit > 0 ? sek.format(row.credit) : '',
  description: row.description,
}));

const draftCents = (value) => {
  const cents = amountToCents(value);
  return cents === null ? 0 : cents;
};

const selectStyles = (isInvalid) => ({
  control: (base, state) => ({
    ...base,
    height: '38px',
    minHeight: '38px',
    flexWrap: 'nowrap',
    borderRadius: '8px',
    fontSize: '0.875rem',
    borderColor: isInvalid ? '#d92d20' : state.isFocused ? '#44814E' : '#aeb6c2',
    boxShadow: isInvalid ? 'inset 0 0 0 1px #d92d20' : state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.20)' : 'none',
    '&:hover': { borderColor: isInvalid ? '#d92d20' : state.isFocused ? '#44814E' : '#8793a1' },
  }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden', padding: '0 0.25rem 0 0.75rem' }),
  placeholder: (base) => ({ ...base, color: '#7e8792', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: '100%' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
});

const BalancePill = ({ debitCents, creditCents, hasAmounts }) => {
  const diff = debitCents - creditCents;
  if (!hasAmounts) return <span className="guided-preview__balance" role="status">Inga belopp</span>;
  if (diff === 0) {
    return <span className="guided-preview__balance is-balanced" role="status"><CheckCircle size={15} /> Balanserar</span>;
  }
  return (
    <span className="guided-preview__balance is-unbalanced" role="status">
      <AlertTriangle size={15} /> Differens {kr(Math.abs(diff) / 100)}
    </span>
  );
};

const PreviewStep = ({
  entry,
  onChange,
  templates = [],
  accounts = [],
  allAccounts = null,
  accountScope = null,
}) => {
  const isNewShape = Boolean(entry.templateData?.body);
  const templateName = entry.templateData?.template_name
    || templates.find((t) => t.id === entry.template)?.name
    || null;

  const proposalRows = useMemo(
    () => (isNewShape ? null : legacyJournalEntries(entry, accounts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNewShape, entry.templateData, entry.parameters, entry.userDefinedAccounts, accounts],
  );
  const rows = entry.customJournalEntries || proposalRows || [];
  const waitingForProposal = isNewShape && !entry.customJournalEntries && !(entry.templateIncompatibility?.length);

  const proposed = useMemo(() => sortByAccount(templateRows(entry, proposalRows)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entry.bookingProposal, proposalRows]);
  const edited = rows.length > 0 && proposed.length > 0 && !sameRows(sortByAccount(rows), proposed);

  const debitCents = rows.reduce((sum, r) => sum + roundCents(r.debit), 0);
  const creditCents = rows.reduce((sum, r) => sum + roundCents(r.credit), 0);
  const warnings = (entry.bookingProposal?.warnings || []).filter(Boolean);
  const facts = answeredInputs(entry, allAccounts || accounts);
  const attachments = entry.attachments || [];

  // ---- Edit mode ---------------------------------------------------------

  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [saveAttempted, setSaveAttempted] = useState(false);

  useEffect(() => {
    // Leaving the step or changing template ends any edit in progress.
    setEditing(false);
    setSaveAttempted(false);
  }, [entry.template]);

  const startEditing = () => {
    setDrafts(toDraftRows(rows));
    setSaveAttempted(false);
    setEditing(true);
  };

  const updateDraft = (key, patch) => {
    setDrafts((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const setDraftAmount = (key, side, raw) => {
    const value = sanitizeAmountInput(raw);
    const other = side === 'debit' ? 'credit' : 'debit';
    updateDraft(key, { [side]: value, ...(draftCents(value) > 0 ? { [other]: '' } : {}) });
  };

  const addDraftRow = () => {
    setDrafts((prev) => [...prev, { key: `new_${Date.now()}`, account: '', accountName: '', debit: '', credit: '' }]);
  };

  const removeDraftRow = (key) => setDrafts((prev) => prev.filter((row) => row.key !== key));

  const resetDrafts = () => setDrafts(toDraftRows(proposed));

  const draftDebit = drafts.reduce((sum, r) => sum + draftCents(r.debit), 0);
  const draftCredit = drafts.reduce((sum, r) => sum + draftCents(r.credit), 0);
  const draftIssues = drafts.flatMap((row, index) => {
    const issues = [];
    if (!row.account) issues.push({ key: row.key, field: 'account', message: `Rad ${index + 1} saknar konto.` });
    if (draftCents(row.debit) === 0 && draftCents(row.credit) === 0) {
      issues.push({ key: row.key, field: 'amount', message: `Rad ${index + 1} saknar belopp.` });
    }
    return issues;
  });
  if (drafts.length < 2) draftIssues.push({ message: 'En kontering behöver minst två rader.' });
  if (drafts.length >= 2 && draftDebit !== draftCredit) {
    draftIssues.push({ message: `Debet och kredit måste vara lika. Differens ${kr(Math.abs(draftDebit - draftCredit) / 100)}.` });
  }
  const draftHasIssue = (key, field) => saveAttempted && draftIssues.some((i) => i.key === key && i.field === field);

  const saveDrafts = () => {
    setSaveAttempted(true);
    if (draftIssues.length > 0) return;
    // Rows stay in the order the user arranged them — cost first, bank last
    // reads better than account-number order for someone checking their work.
    const saved = drafts.map((row, index) => ({
      id: row.key,
      account: row.account,
      accountName: row.accountName,
      debit: draftCents(row.debit) / 100,
      credit: draftCents(row.credit) / 100,
      description: row.description || entry.description || '',
      originalIndex: index,
    }));
    onChange({ ...entry, customJournalEntries: saved });
    setEditing(false);
  };

  // ---- Render ------------------------------------------------------------

  const renderAccountCell = (row) => {
    if (!row.account || row.account === UNSET_ACCOUNT) {
      return <span className="guided-preview__account-missing">Konto ej valt</span>;
    }
    return (
      <>
        <span className="guided-preview__account-number">{row.account}</span>
        <span className="guided-preview__account-name">{row.accountName}</span>
      </>
    );
  };

  const renderReadTable = () => {
    if (waitingForProposal) {
      return (
        <div className="guided-preview__loading" role="status">
          <Spinner animation="border" size="sm" />
          Räknar fram konteringen…
        </div>
      );
    }
    if (rows.length === 0) {
      return <p className="guided-preview__empty">Ingen kontering kunde tas fram. Gå tillbaka och kontrollera uppgifterna.</p>;
    }
    return (
      <div className="guided-preview__table-wrap">
        <table className="guided-preview__table">
          <thead>
            <tr>
              <th scope="col">Konto</th>
              <th scope="col" className="is-amount">Debet</th>
              <th scope="col" className="is-amount">Kredit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || `${row.account}-${index}`}>
                <td>{renderAccountCell(row)}</td>
                <td className="is-amount">{row.debit > 0 ? kr(row.debit) : <span className="is-empty">–</span>}</td>
                <td className="is-amount">{row.credit > 0 ? kr(row.credit) : <span className="is-empty">–</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Summa</td>
              <td className="is-amount">{kr(debitCents / 100)}</td>
              <td className="is-amount">{kr(creditCents / 100)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderEditor = () => (
    <div className="guided-preview__editor">
      {accountScope && <AccountPickerScope {...accountScope} style={{ marginTop: 0, marginBottom: '0.75rem' }} />}
      <table className="guided-preview__edit-table">
        <thead>
          <tr>
            <th scope="col">Konto</th>
            <th scope="col" style={{ textAlign: 'right' }}>Debet</th>
            <th scope="col" style={{ textAlign: 'right' }}>Kredit</th>
            <th scope="col"><span className="visually-hidden">Radåtgärder</span></th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((row, index) => {
            const selected = accounts.find((acc) => acc.value === row.account)
              || (allAccounts || []).find((acc) => acc.value === row.account)
              || null;
            return (
              <tr key={row.key}>
                <td>
                  <AccountSelect
                    inputId={`preview-account-${index}`}
                    aria-label={`Konto på rad ${index + 1}`}
                    options={accounts}
                    allOptions={allAccounts || accounts}
                    value={selected}
                    onChange={(opt) => updateDraft(row.key, {
                      account: opt ? opt.value : '',
                      accountName: opt ? (opt.accountName || '') : '',
                    })}
                    valueDisplay="label"
                    placeholder="Välj konto…"
                    styles={selectStyles(draftHasIssue(row.key, 'account'))}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`form-control guided-preview__amount-input ${draftHasIssue(row.key, 'amount') ? 'is-invalid' : ''}`}
                    aria-label={`Debet på rad ${index + 1}`}
                    value={row.debit}
                    placeholder="0,00"
                    disabled={draftCents(row.credit) > 0}
                    onChange={(e) => setDraftAmount(row.key, 'debit', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`form-control guided-preview__amount-input ${draftHasIssue(row.key, 'amount') ? 'is-invalid' : ''}`}
                    aria-label={`Kredit på rad ${index + 1}`}
                    value={row.credit}
                    placeholder="0,00"
                    disabled={draftCents(row.debit) > 0}
                    onChange={(e) => setDraftAmount(row.key, 'credit', e.target.value)}
                  />
                </td>
                <td className="text-center">
                  <button
                    type="button"
                    className="guided-preview__remove-row"
                    onClick={() => removeDraftRow(row.key)}
                    aria-label={`Ta bort rad ${index + 1}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Summa</td>
            <td className="guided-preview__amount-input">{kr(draftDebit / 100)}</td>
            <td className="guided-preview__amount-input">{kr(draftCredit / 100)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      {saveAttempted && draftIssues.length > 0 && (
        <div className="guided-preview__row-error" role="alert">{draftIssues[0].message}</div>
      )}

      <div className="guided-preview__editor-actions">
        <div>
          <button type="button" className="guided-preview__text-action" onClick={addDraftRow}>
            <Plus size={15} /> Lägg till rad
          </button>
          <button type="button" className="guided-preview__text-action" onClick={resetDrafts}>
            Återställ mallens förslag
          </button>
        </div>
        <div>
          <BalancePill debitCents={draftDebit} creditCents={draftCredit} hasAmounts={draftDebit + draftCredit > 0} />
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(false)}>
            Avbryt
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={saveDrafts}>
            Spara kontering
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="guided-preview">
      <section className="guided-preview__section guided-preview__summary" aria-label="Uppgifter">
        <h3 className="guided-preview__title">{entry.description || templateName || 'Verifikat'}</h3>
        <p className="guided-preview__meta">
          <span>{entry.date ? format(entry.date, 'd MMMM yyyy', { locale: sv }) : 'Inget datum'}</span>
          {entry.reference && <span>Referens {entry.reference}</span>}
          {templateName && entry.description !== templateName && <span>Mall {templateName}</span>}
        </p>

        {facts.length > 0 && (
          <dl className="guided-preview__facts">
            {facts.map((fact) => (
              <div key={fact.name} className="guided-preview__fact">
                <dt title={fact.fullLabel !== fact.label ? fact.fullLabel : undefined}>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {entry.notes && entry.notes.trim() && (
          <p className="guided-preview__note"><span>Kommentar</span>{entry.notes}</p>
        )}

        {attachments.length > 0 && (
          <ul className="guided-preview__files" aria-label="Underlag">
            {attachments.map((file) => (
              <li key={file.id || file.name} className="guided-preview__file" title={file.name}>
                <span>{file.name}</span>
                <small>{formatFileSize(file.size)}</small>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="guided-preview__section" aria-labelledby="guided-preview-posting-heading">
        <div className="guided-preview__section-heading">
          <div>
            <h3 id="guided-preview-posting-heading" className="guided-preview__section-title">Så bokförs det</h3>
            <p className="guided-preview__section-hint">
              {edited
                ? 'Konteringen är justerad av dig. Återställ mallens förslag under Ändra kontering om du vill börja om.'
                : 'Förslaget kommer från mallen. Stämmer raderna är det bara att skapa verifikatet.'}
            </p>
          </div>
          {!editing && !waitingForProposal && rows.length > 0 && (
            <div className="guided-preview__heading-actions">
              <BalancePill debitCents={debitCents} creditCents={creditCents} hasAmounts={debitCents + creditCents > 0} />
              <button type="button" className="guided-preview__text-action" onClick={startEditing}>
                <Pencil size={14} /> Ändra kontering
              </button>
            </div>
          )}
        </div>

        {editing ? renderEditor() : renderReadTable()}

        {!editing && warnings.length > 0 && (
          <div className="guided-preview__warning" role="note">
            {warnings.map((warning, index) => <p key={index}>{warning}</p>)}
          </div>
        )}
      </section>
    </div>
  );
};

export default PreviewStep;
